'use client';

import { fetchWithAuth, type FetchWithAuthInit } from '@/lib/utils/api-auth';

// AuthProviderId enumerates the social-login providers the API
// exposes via /api/auth/social/{provider}. M1 added Microsoft for
// Office365 / Entra ID customers; the literal string is used both
// for the route segment AND for the i18n key suffix
// (modals.auth.continueWith{Microsoft|Google|GitHub}).
export type AuthProviderId = 'google' | 'github' | 'microsoft';
export type PasswordTokenKind = 'password-setup' | 'reset-password';

type ApiEnvelope<T = Record<string, unknown>> = {
  success?: boolean;
  error?: string;
  code?: string;
  message?: string;
  // Servers emit camelCase (retryAfterSeconds). The legacy snake_case key stays
  // accepted by parseEnvelopeRetryAfter so pre-migration clients keep working.
  retryAfterSeconds?: number;
  retry_after_seconds?: number;
  data?: T;
};

type ApiSuccess<T> = {
  ok: true;
  status: number;
  data: T;
  message?: string;
};

type ApiFailure = {
  ok: false;
  status: number;
  error: string;
  code?: string;
  retryAfterSeconds?: number;
  unavailable?: boolean;
};

type ApiResult<T> = ApiSuccess<T> | ApiFailure;

export type AuthApiUser = {
  id: string;
  email: string;
  name: string | null;
  role: string;
  emailVerifiedAt?: string | null;
  hasPassword?: boolean;
  twoFactorEnabled?: boolean;
  twoFactorPendingSetup?: boolean;
  authProviders?: string[];
  /**
   * Server-side flag mirroring users.must_re_enroll_passkey. When true the
   * user consumed a passkey recovery link and is gated behind the
   * re-enrollment wall until they register a replacement credential. The
   * frontend reads this to keep the mustEnroll banner visible across
   * reloads (the `?mustEnroll=1` query string is only a first-hop signal).
   */
  mustReEnrollPasskey?: boolean;
};

export type PendingTwoFactorChallenge = {
  challengeToken: string;
  expiresAt?: string | null;
  pendingSetup?: boolean;
  email?: string;
};

export type AuthFlowSuccess = {
  ok: true;
  user?: AuthApiUser | null;
  message?: string;
  requiresEmailVerification?: boolean;
  verificationEmailSentTo?: string;
  requiresPasswordSetup?: boolean;
  passwordSetupEmailSentTo?: string;
  nextStep?: 'authenticated' | 'email-verification' | 'password-setup' | 'two-factor';
  twoFactorChallenge?: PendingTwoFactorChallenge;
};

export type AuthFlowFailure = {
  ok: false;
  error: string;
  code?: string;
  retryAfterSeconds?: number;
  unavailable?: boolean;
};

export type AuthFlowResult = AuthFlowSuccess | AuthFlowFailure;

export type SocialAuthStartResult =
  | {
      ok: true;
      redirectUrl: string;
      provider: AuthProviderId;
    }
  | AuthFlowFailure;

export type PasswordTokenValidationResult =
  | {
      ok: true;
      valid: boolean;
      kind: PasswordTokenKind;
    }
  | AuthFlowFailure;

export type TwoFactorSetupPayload = {
  secret: string;
  provisioningUri: string;
  expiresAt?: string;
};

export type TwoFactorStatusResult =
  | {
      ok: true;
      enabled: boolean | null;
      pendingSetup: boolean | null;
      recoveryCodesRemaining?: number | null;
    }
  | AuthFlowFailure;

type AuthMutationPayload = {
  user?: unknown;
  message?: string;
  requiresEmailVerification?: boolean;
  verificationEmailSentTo?: string;
  requiresPasswordSetup?: boolean;
  passwordSetupEmailSentTo?: string;
  requiresTwoFactor?: boolean;
  twoFactorChallengeToken?: string;
  twoFactorChallengeExpiresAt?: string;
  twoFactorPendingSetup?: boolean;
};

const FEATURE_UNAVAILABLE_STATUSES = new Set([404, 405, 501]);

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const getString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const getBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

const getStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  return items.length > 0 ? items : undefined;
};

const parseEnvelopeData = <T>(payload: unknown): T | undefined => {
  if (!isObject(payload) || !('data' in payload)) {
    return undefined;
  }
  return payload.data as T | undefined;
};

const parseEnvelopeMessage = (payload: unknown): string | undefined => {
  if (!isObject(payload)) return undefined;
  const topLevel = getString(payload.message);
  if (topLevel) return topLevel;
  const data = parseEnvelopeData<Record<string, unknown>>(payload);
  return data ? getString(data.message) : undefined;
};

const parseEnvelopeError = (payload: unknown): string | undefined => {
  if (!isObject(payload)) return undefined;
  return getString(payload.error);
};

const parseEnvelopeCode = (payload: unknown): string | undefined => {
  if (!isObject(payload)) return undefined;
  return getString(payload.code);
};

const parseEnvelopeRetryAfter = (payload: unknown): number | undefined => {
  if (!isObject(payload)) return undefined;
  const camel = payload.retryAfterSeconds;
  if (typeof camel === 'number' && Number.isFinite(camel)) return camel;
  const snake = payload.retry_after_seconds;
  return typeof snake === 'number' && Number.isFinite(snake) ? snake : undefined;
};

const parseAuthUser = (value: unknown): AuthApiUser | null => {
  if (!isObject(value)) return null;

  const id = getString(value.id);
  const email = getString(value.email);
  const role = getString(value.role);
  if (!id || !email || !role) return null;

  return {
    id,
    email,
    role,
    name: typeof value.name === 'string' ? value.name : null,
    emailVerifiedAt: typeof value.emailVerifiedAt === 'string' ? value.emailVerifiedAt : null,
    hasPassword: getBoolean(value.hasPassword),
    twoFactorEnabled: getBoolean(value.twoFactorEnabled),
    twoFactorPendingSetup: getBoolean(value.twoFactorPendingSetup),
    authProviders: getStringArray(value.authProviders),
  };
};

async function requestApi<T>(path: string, init: FetchWithAuthInit = {}): Promise<ApiResult<T>> {
  try {
    const response = await fetchWithAuth(path, {
      cache: 'no-store',
      ...init,
    });

    const payload = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
    const success = payload?.success !== false && response.ok;

    if (!success) {
      return {
        ok: false,
        status: response.status,
        error: parseEnvelopeError(payload) ?? `Request failed (${response.status})`,
        code: parseEnvelopeCode(payload),
        retryAfterSeconds: parseEnvelopeRetryAfter(payload),
        unavailable: FEATURE_UNAVAILABLE_STATUSES.has(response.status),
      };
    }

    return {
      ok: true,
      status: response.status,
      data: parseEnvelopeData<T>(payload) ?? ({} as T),
      message: parseEnvelopeMessage(payload),
    };
  } catch {
    return {
      ok: false,
      status: 0,
      error: 'Network error. Please try again.',
    };
  }
}

function toAuthFailure(result: ApiFailure, fallback: string): AuthFlowFailure {
  return {
    ok: false,
    error: result.error || fallback,
    code: result.code,
    retryAfterSeconds: result.retryAfterSeconds,
    unavailable: result.unavailable,
  };
}

function mapAuthMutationResult(
  result: ApiResult<AuthMutationPayload>,
  fallbackError: string,
  emailHint?: string,
): AuthFlowResult {
  if (!result.ok) {
    return toAuthFailure(result, fallbackError);
  }

  const payload = result.data ?? {};
  const user = parseAuthUser(payload.user);

  if (payload.requiresTwoFactor && getString(payload.twoFactorChallengeToken)) {
    return {
      ok: true,
      user,
      message: getString(payload.message) ?? result.message,
      nextStep: 'two-factor',
      twoFactorChallenge: {
        challengeToken: payload.twoFactorChallengeToken!,
        expiresAt: getString(payload.twoFactorChallengeExpiresAt) ?? null,
        pendingSetup: payload.twoFactorPendingSetup === true,
        email: emailHint,
      },
    };
  }

  if (payload.requiresPasswordSetup || getString(payload.passwordSetupEmailSentTo)) {
    return {
      ok: true,
      user,
      message: getString(payload.message) ?? result.message,
      requiresPasswordSetup: true,
      passwordSetupEmailSentTo: getString(payload.passwordSetupEmailSentTo) ?? emailHint,
      nextStep: 'password-setup',
    };
  }

  if (payload.requiresEmailVerification) {
    return {
      ok: true,
      user,
      message: getString(payload.message) ?? result.message,
      requiresEmailVerification: true,
      verificationEmailSentTo: getString(payload.verificationEmailSentTo) ?? emailHint,
      nextStep: 'email-verification',
    };
  }

  return {
    ok: true,
    user,
    message: getString(payload.message) ?? result.message,
    nextStep: 'authenticated',
  };
}

export async function loginWithPassword(input: { email: string; password: string }): Promise<AuthFlowResult> {
  const result = await requestApi<AuthMutationPayload>('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  return mapAuthMutationResult(result, 'Login failed.', input.email);
}

export async function registerWithEmail(input: {
  email: string;
  name?: string;
  password?: string;
}): Promise<AuthFlowResult> {
  const result = await requestApi<AuthMutationPayload>('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!result.ok && !input.password) {
    const normalized = result.error.toLowerCase();
    if (
      result.status === 400 &&
      (normalized.includes('invalid registration payload') || normalized.includes('password'))
    ) {
      return {
        ok: false,
        error: 'Email-first sign-up is not available in this environment yet.',
        code: result.code,
        retryAfterSeconds: result.retryAfterSeconds,
        unavailable: true,
      };
    }
  }

  return mapAuthMutationResult(result, 'Registration failed.', input.email);
}

export async function requestPasswordResetEmail(input: { email: string }): Promise<AuthFlowResult> {
  const result = await requestApi<{ message?: string }>('/api/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!result.ok) {
    return toAuthFailure(result, 'Password reset request failed.');
  }

  return {
    ok: true,
    message:
      getString(result.data?.message) ??
      result.message ??
      'If an account exists for this email, a password reset link has been sent.',
  };
}

async function validatePasswordTokenOnce(
  token: string,
  kind: PasswordTokenKind,
): Promise<ApiResult<{ valid?: boolean }>> {
  return requestApi<{ valid?: boolean }>(`/api/auth/${kind}/validate?token=${encodeURIComponent(token)}`, {
    cache: 'no-store',
  });
}

export async function validatePasswordToken(
  token: string,
  preferredKind: PasswordTokenKind,
): Promise<PasswordTokenValidationResult> {
  const orderedKinds: PasswordTokenKind[] =
    preferredKind === 'password-setup' ? ['password-setup', 'reset-password'] : ['reset-password', 'password-setup'];

  let fallbackFailure: ApiFailure | null = null;
  for (const kind of orderedKinds) {
    const result = await validatePasswordTokenOnce(token, kind);
    if (result.ok) {
      return {
        ok: true,
        valid: result.data?.valid === true,
        kind,
      };
    }
    if (!FEATURE_UNAVAILABLE_STATUSES.has(result.status)) {
      fallbackFailure = result;
      break;
    }
  }

  if (fallbackFailure) {
    return toAuthFailure(fallbackFailure, 'Failed to validate link.');
  }

  return {
    ok: false,
    error: 'Password setup is not available in this environment yet.',
    unavailable: true,
  };
}

async function submitPasswordTokenOnce(input: {
  token: string;
  password: string;
  kind: PasswordTokenKind;
}): Promise<ApiResult<{ message?: string }>> {
  return requestApi<{ message?: string }>(`/api/auth/${input.kind}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: input.token, password: input.password }),
  });
}

export async function submitPasswordToken(input: {
  token: string;
  password: string;
  preferredKind: PasswordTokenKind;
}): Promise<AuthFlowResult> {
  const orderedKinds: PasswordTokenKind[] =
    input.preferredKind === 'password-setup'
      ? ['password-setup', 'reset-password']
      : ['reset-password', 'password-setup'];

  let fallbackFailure: ApiFailure | null = null;
  for (const kind of orderedKinds) {
    const result = await submitPasswordTokenOnce({ ...input, kind });
    if (result.ok) {
      return {
        ok: true,
        message:
          getString(result.data?.message) ??
          result.message ??
          (kind === 'password-setup'
            ? 'Password set. You can now log in with your new password.'
            : 'Password updated. You can now log in with your new password.'),
      };
    }
    if (!FEATURE_UNAVAILABLE_STATUSES.has(result.status)) {
      fallbackFailure = result;
      break;
    }
  }

  if (fallbackFailure) {
    return toAuthFailure(fallbackFailure, 'Failed to update password.');
  }

  return {
    ok: false,
    error: 'Password setup is not available in this environment yet.',
    unavailable: true,
  };
}

// providerDisplayName converts the wire provider id to its branding
// name so error messages can name the upstream IdP. Centralised here
// so the AuthModal does not have to duplicate the mapping.
function providerDisplayName(provider: AuthProviderId): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'github':
      return 'GitHub';
    case 'microsoft':
      return 'Microsoft';
  }
}

export async function startSocialAuth(provider: AuthProviderId, returnTo: string): Promise<SocialAuthStartResult> {
  const result = await requestApi<{ url?: string; provider?: string }>(
    `/api/auth/social/${provider}?return_to=${encodeURIComponent(returnTo)}`,
    {
      method: 'GET',
    },
  );

  if (!result.ok) {
    return {
      ok: false,
      error: result.error || `${providerDisplayName(provider)} sign-in is unavailable right now.`,
      code: result.code,
      retryAfterSeconds: result.retryAfterSeconds,
      unavailable: result.unavailable,
    };
  }

  const redirectUrl = getString(result.data?.url);
  if (!redirectUrl) {
    return {
      ok: false,
      error: `${providerDisplayName(provider)} sign-in is unavailable right now.`,
      unavailable: true,
    };
  }

  return {
    ok: true,
    redirectUrl,
    provider,
  };
}

export async function verifyTwoFactorLogin(input: {
  challengeToken: string;
  code?: string;
  recoveryCode?: string;
  // M3 — when true, the backend mints a TrustedDevice row +
  // sets the trust cookie so future logins from this browser
  // skip the TOTP challenge for 30 days.  Default off so the
  // opt-in is explicit.
  rememberDevice?: boolean;
}): Promise<AuthFlowResult> {
  const result = await requestApi<AuthMutationPayload>('/api/auth/two-factor/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  return mapAuthMutationResult(result, 'Two-factor verification failed.');
}

// M3 — Trusted-device row shape returned by the list endpoint.
// Matches the JSON the Go handler emits so the settings UI can
// render without a normalisation step.
export type TrustedDeviceRow = {
  id: string;
  deviceLabel?: string;
  uaFamily?: string;
  osFamily?: string;
  ipHash?: string;
  country?: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string;
  // True for the row whose cookie matches the presenting
  // browser. The settings UI uses this to render a "this
  // device" pill so the user doesn't accidentally revoke the
  // session they're sitting in.
  current: boolean;
};

export type TrustedDevicesResult = { ok: true; items: TrustedDeviceRow[] } | AuthFlowFailure;

export async function loadTrustedDevices(): Promise<TrustedDevicesResult> {
  const result = await requestApi<{ items?: unknown }>('/api/auth/2fa/trusted-devices', {
    method: 'GET',
    cache: 'no-store',
  });
  if (!result.ok) {
    return toAuthFailure(result, 'Failed to load trusted devices.');
  }
  const raw = Array.isArray(result.data?.items) ? result.data.items : [];
  const items: TrustedDeviceRow[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const id = getString(entry.id);
    const createdAt = getString(entry.createdAt);
    const expiresAt = getString(entry.expiresAt);
    const lastUsedAt = getString(entry.lastUsedAt);
    if (!id || !createdAt || !expiresAt || !lastUsedAt) continue;
    items.push({
      id,
      deviceLabel: getString(entry.deviceLabel),
      uaFamily: getString(entry.uaFamily),
      osFamily: getString(entry.osFamily),
      ipHash: getString(entry.ipHash),
      country: getString(entry.country),
      createdAt,
      expiresAt,
      lastUsedAt,
      current: getBoolean(entry.current) ?? false,
    });
  }
  return { ok: true, items };
}

export async function revokeTrustedDevice(id: string): Promise<AuthFlowResult> {
  const result = await requestApi<{ id?: string }>(`/api/auth/2fa/trusted-devices/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!result.ok) {
    return toAuthFailure(result, 'Failed to revoke trusted device.');
  }
  return { ok: true, message: result.message };
}

export async function revokeAllTrustedDevices(): Promise<AuthFlowResult> {
  const result = await requestApi<{ count?: number }>('/api/auth/2fa/trusted-devices/revoke-all', {
    method: 'POST',
  });
  if (!result.ok) {
    return toAuthFailure(result, 'Failed to revoke trusted devices.');
  }
  return { ok: true, message: result.message };
}

export async function loadTwoFactorStatus(): Promise<TwoFactorStatusResult> {
  const result = await requestApi<Record<string, unknown>>('/api/auth/two-factor/status', {
    method: 'GET',
    cache: 'no-store',
  });

  if (!result.ok) {
    return toAuthFailure(result, 'Failed to load two-factor status.');
  }

  return {
    ok: true,
    enabled: getBoolean(result.data.enabled) ?? null,
    pendingSetup: getBoolean(result.data.pendingSetup) ?? null,
    recoveryCodesRemaining:
      typeof result.data.recoveryCodesRemaining === 'number' ? result.data.recoveryCodesRemaining : null,
  };
}

export async function beginTwoFactorSetup(): Promise<
  | {
      ok: true;
      data: TwoFactorSetupPayload;
      message?: string;
    }
  | AuthFlowFailure
> {
  const result = await requestApi<TwoFactorSetupPayload>('/api/auth/two-factor/setup', {
    method: 'POST',
  });

  if (!result.ok) {
    return toAuthFailure(result, 'Failed to start two-factor setup.');
  }

  return {
    ok: true,
    data: {
      secret: getString(result.data.secret) ?? '',
      provisioningUri: getString(result.data.provisioningUri) ?? '',
      expiresAt: getString(result.data.expiresAt),
    },
    message: result.message,
  };
}

export async function confirmTwoFactorSetup(input: { code: string }): Promise<
  | {
      ok: true;
      recoveryCodes: string[];
      message?: string;
    }
  | AuthFlowFailure
> {
  const result = await requestApi<{ recoveryCodes?: unknown; message?: string }>('/api/auth/two-factor/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!result.ok) {
    return toAuthFailure(result, 'Failed to confirm two-factor setup.');
  }

  return {
    ok: true,
    recoveryCodes: getStringArray(result.data?.recoveryCodes) ?? [],
    message: getString(result.data?.message) ?? result.message,
  };
}

export async function disableTwoFactorAuth(input: {
  password?: string;
  code?: string;
  recoveryCode?: string;
}): Promise<AuthFlowResult> {
  const result = await requestApi<{ message?: string }>('/api/auth/two-factor/disable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!result.ok) {
    return toAuthFailure(result, 'Failed to disable two-factor authentication.');
  }

  return {
    ok: true,
    message: getString(result.data?.message) ?? result.message ?? 'Two-factor authentication disabled.',
  };
}

export async function regenerateTwoFactorRecoveryCodes(): Promise<
  | {
      ok: true;
      recoveryCodes: string[];
      message?: string;
    }
  | AuthFlowFailure
> {
  const candidates = ['/api/auth/two-factor/recovery-codes/regenerate', '/api/auth/two-factor/recovery/regenerate'];

  let fallbackFailure: ApiFailure | null = null;
  for (const path of candidates) {
    const result = await requestApi<{ recoveryCodes?: unknown; message?: string }>(path, {
      method: 'POST',
    });
    if (result.ok) {
      return {
        ok: true,
        recoveryCodes: getStringArray(result.data?.recoveryCodes) ?? [],
        message: getString(result.data?.message) ?? result.message,
      };
    }
    if (!FEATURE_UNAVAILABLE_STATUSES.has(result.status)) {
      fallbackFailure = result;
      break;
    }
  }

  if (fallbackFailure) {
    return toAuthFailure(fallbackFailure, 'Failed to regenerate recovery codes.');
  }

  return {
    ok: false,
    error: 'Recovery code regeneration is still rolling out on the backend.',
    unavailable: true,
  };
}
